import { FC, ReactNode, useEffect } from 'react'
import { useDisclosure } from '@sequencehq/utils'
import {
  BulkActionBar,
  Popover,
  ShadowL,
  useModalState,
  useNotifications,
  IconArchive,
  IconArrowInbox
} from '@sequencehq/core-components'
import {
  dashboard20240730Client,
  type DashboardApi20240730
} from '@sequencehq/api/dashboard/v20240730'
import { useMutation } from '@sequencehq/api'
import { ProtectedButton } from 'lib/rbac'
import { ArchiveQuotesDialog } from './archiveQuotes.dialog'

/**
 * The bulk-download endpoint rejects a request for more than this many quotes.
 * The download action is disabled above the cap so the user sees why up front
 * instead of triggering a request the backend will reject.
 */
const MAX_DOWNLOAD_QUOTES = 200

type Quote = DashboardApi20240730.GetQuotes.Quote

type Props = {
  children: ReactNode
  selectedRows: Quote[]
  clearSelectedRows: () => void
  popoverOffset?: [number, number]
}

export const QuoteBulkActions: FC<Props> = ({
  children,
  selectedRows,
  clearSelectedRows,
  popoverOffset
}) => {
  const { isOpen, onToggle, onClose } = useDisclosure()
  const { displayNotification } = useNotifications()
  const { isModalOpen, openModal, closeModal } = useModalState()
  const downloadQuotes = useMutation(
    dashboard20240730Client.postDownloadMultipleQuotes
  )

  // A draft quote has no published variant, so the backend has no stored PDF
  // for it and skips it (see DownloadQuotePdfBulkActionTask). Exclude drafts
  // from the count and the request up front so the count reflects what will
  // actually download. Unlike invoices, which have a PDF in any status, a
  // quote's PDF only exists once it has been published.
  const downloadableRows = selectedRows.filter(
    quote => quote.status !== 'DRAFT'
  )
  const downloadableCount = downloadableRows.length
  const skippedDraftCount = selectedRows.length - downloadableCount
  const isOverDownloadCap = downloadableCount > MAX_DOWNLOAD_QUOTES

  const downloadTooltip = isOverDownloadCap
    ? `A maximum of ${MAX_DOWNLOAD_QUOTES} quotes can be downloaded`
    : undefined

  // Every non-archived quote is eligible; the backend skips already-archived
  // ids idempotently, but we exclude them up-front so the count is accurate.
  const archivableRows = selectedRows.filter(quote => !quote.archivedAt)
  const archivableCount = archivableRows.length
  const showSignatureWarning = archivableRows.some(
    quote => quote.status === 'READY_TO_SIGN'
  )

  useEffect(() => {
    if (selectedRows.length > 0 && !isOpen) {
      onToggle()
    }
    if (selectedRows.length === 0 && isOpen) {
      onClose()
    }
  }, [selectedRows, isOpen, onToggle, onClose])

  const archiveSelectedQuotes = async () => {
    const res = await dashboard20240730Client.postArchiveMultipleQuotes({
      quoteIds: archivableRows.map(quote => quote.id)
    })

    if (res.error) {
      displayNotification('Unable to archive quotes', { type: 'error' })
      return false
    }

    clearSelectedRows()
    onClose()
    return true
  }

  const downloadSelectedQuotes = async () => {
    const quoteIds = downloadableRows.map(quote => quote.id)

    // Clear the selection up front (ids are already captured). This unmounts the
    // bar instantly via the empty-selection guard below, so it doesn't linger
    // over the async request while the in-flight download panel loads.
    clearSelectedRows()

    try {
      await downloadQuotes.mutateAsync({ quoteIds })
    } catch {
      displayNotification('Unable to download quotes', { type: 'error' })
    }
  }

  const handleClose = () => {
    clearSelectedRows()
    onClose()
  }

  // Show the eligible-count badge only when the selection contains
  // ineligible (already-archived) quotes.
  const showCount =
    archivableCount !== selectedRows.length && archivableCount !== 0

  // The status bar always renders this component (even with nothing selected),
  // relying on the popover's isOpen to hide it. Closing via isOpen plays the
  // popover's exit fade, leaving a greyed-out ghost of the bar. Returning null
  // unmounts the popover outright, so clearing the selection removes the bar
  // instantly with no fade.
  if (selectedRows.length === 0) {
    return null
  }

  return (
    <Popover.Root
      isOpen={isOpen}
      onClose={onClose}
      placement="top"
      closeOnBlur={false}
      offset={popoverOffset}
    >
      <Popover.Anchor>{children}</Popover.Anchor>
      <Popover.Content
        width="100%"
        py={1}
        px={2}
        borderRadius="8px"
        boxShadow={ShadowL}
      >
        <Popover.Body width="100%" height="100%" p="0">
          <BulkActionBar count={selectedRows.length} onDismiss={handleClose}>
            <ProtectedButton permission="org.quote:archive">
              <BulkActionBar.Button
                data-testid="quotes.bulkActions.archive"
                icon={<IconArchive />}
                showCount={showCount}
                count={archivableCount}
                onClick={openModal}
              >
                Archive
              </BulkActionBar.Button>
            </ProtectedButton>
            <ArchiveQuotesDialog
              isOpen={isModalOpen}
              onClose={closeModal}
              count={archivableCount}
              showSignatureWarning={showSignatureWarning}
              onSubmit={archiveSelectedQuotes}
            />
            <ProtectedButton permission="org.quote:read">
              <BulkActionBar.Button
                data-testid="quotes.bulkActions.download"
                icon={<IconArrowInbox />}
                showCount={skippedDraftCount > 0 && downloadableCount !== 0}
                count={downloadableCount}
                isDisabled={isOverDownloadCap}
                tooltip={downloadTooltip}
                onClick={() => void downloadSelectedQuotes()}
              >
                Download PDFs
              </BulkActionBar.Button>
            </ProtectedButton>
          </BulkActionBar>
        </Popover.Body>
      </Popover.Content>
    </Popover.Root>
  )
}
